import React, { memo, useState, useEffect } from 'react';
import { Handle, Position } from 'reactflow';
import api from '../utils/api';
import {
  HiArrowNarrowRight,
  HiBookOpen,
  HiClock,
  HiCog,
  HiCursorClick,
  HiDatabase,
  HiGlobeAlt,
  HiLogout,
  HiPhone,
  HiPlay,
} from 'react-icons/hi';

// Node type icons mapping - shared with AddNodePanel
export const NODE_ICONS = {
  'Default': HiPhone,
  'End Call': HiLogout,
  'Transfer Call': HiArrowNarrowRight,
  'Vector DB Knowledge Base': HiBookOpen,
  'Webhook': HiGlobeAlt,
  'Transfer Dialog Flow': HiDatabase,
  'Custom Tool': HiCog,
  'Press Button': HiCursorClick,
  'Wait for Response': HiClock
};

const CustomNode = ({ data, isConnectable }) => {
  const [knowledgeBaseName, setKnowledgeBaseName] = useState('');
  const isGlobalNode = data.isGlobal;
  const isStartNode = data.isStart;
  const isEndNode = data.type === 'End Call';
  const isTransferNode = data.type === 'Transfer Call';
  const isKnowledgeBaseNode = data.type === 'Vector DB Knowledge Base';
  
  // Fetch knowledge base name when kbTool changes
  useEffect(() => {
    const fetchKnowledgeBaseName = async () => {
      if (isKnowledgeBaseNode && data.kbTool) {
        try {
          const response = await api.getKnowledgeBases();
          const kb = response.data.vectors.find(kb => kb.vector_id === data.kbTool);
          setKnowledgeBaseName(kb?.name || 'Unknown Knowledge Base');
        } catch (error) {
          console.error('Error fetching knowledge base:', error);
          setKnowledgeBaseName('Error loading KB name');
        }
      }
    };

    fetchKnowledgeBaseName();
  }, [isKnowledgeBaseNode, data.kbTool]);

  // Function to get the correct icon based on node type
  const getNodeIcon = () => {
    // Try to get icon by data.type (from NodeModal)
    if (data.type && NODE_ICONS[data.type]) {
      return NODE_ICONS[data.type];
    }
    // Default to default node icon
    return NODE_ICONS['Default'];
  };
  
  // Get the display text (either text or prompt)
  const displayText = data.text || data.prompt || '';
  const hasMoreText = displayText.length > 50;
  
  // Get the icon based on node type
  const NodeIcon = getNodeIcon();
  
  return (
    <div 
      className={`dialog-node relative ${
        isGlobalNode ? 'dialog-node-global' : ''
      } ${
        isStartNode ? 'dialog-node-start' : ''
      } ${
        isEndNode ? 'dialog-node-end' : ''
      }`}
      style={{ width: '320px', minHeight: '96px' }}
    >
      {/* Only show input handle if not a global node and not a start node */}
      {!isGlobalNode && !isStartNode && (
        <Handle
          type="target"
          position={Position.Top}
          isConnectable={isConnectable}
          className="dialog-node-handle dialog-node-handle-target"
        />
      )}
      
      <div className="p-4">
        <div className="flex items-start gap-3">
          <div className="dialog-node-icon">
            {isStartNode ? (
              <HiPlay className="w-5 h-5" />
            ) : (
              <NodeIcon className="w-5 h-5" />
            )}
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-start gap-2">
              <div className="dialog-node-title">{data.name}</div>
              {isGlobalNode && (
                <span className="dialog-node-badge">
                  Global
                </span>
              )}
            </div>
            {isGlobalNode && data.globalLabel && (
              <div className="dialog-node-meta mt-1 italic">
                Trigger: {data.globalLabel}
              </div>
            )}
            {displayText && (
              <div className="dialog-node-copy mt-1 group relative">
                <div className="line-clamp-2">{displayText}</div>
                {hasMoreText && (
                  <div className="dialog-node-tooltip">
                    {displayText}
                  </div>
                )}
              </div>
            )}
            {isTransferNode && data.transferNumber && (
              <div className="dialog-node-chip mt-3">
                {data.transferNumber}
              </div>
            )}
            {isKnowledgeBaseNode && knowledgeBaseName && (
              <div className="dialog-node-chip mt-3 group relative">
                <div className="truncate max-w-[200px]">{knowledgeBaseName}</div>
                {knowledgeBaseName.length > 30 && (
                  <div className="dialog-node-tooltip whitespace-nowrap">
                    {knowledgeBaseName}
                  </div>
                )}
              </div>
            )}
          </div>
          <div className="dialog-node-type">
            {data.type}
          </div>
        </div>
      </div>

      {/* Output handle is shown for all nodes except end nodes and transfer nodes */}
      {!isEndNode && !isTransferNode && !isGlobalNode && (
        <Handle
          type="source"
          position={Position.Bottom}
          isConnectable={isConnectable}
          className="dialog-node-handle dialog-node-handle-source"
        />
      )}
    </div>
  );
};

export default memo(CustomNode);
